// Feb 2026 · R281 · Visibility-aware polling helper.
//
// Every shell / header component today wraps its data fetch in a bare
// `setInterval(load, N)`. That means every open tab hammers the backend
// even when the user has minimised the window or switched to another
// tab — a ~200-request-per-46s storm identified in the post-deploy
// latency RCA.
//
// This helper does two things:
//   1. Only ticks while the tab is actually visible.
//   2. Immediately fires a catch-up run when the tab becomes visible
//      again after being hidden, so freshness is restored the moment
//      the user's attention returns.
//
// Usage in a React component:
//
//   useEffect(() => {
//     load();                                   // initial run
//     return pollWhenVisible(load, 30_000);     // returns a cleanup fn
//   }, [deps]);
//
// The returned cleanup removes both the timer and the visibilitychange
// listener, so it can be used directly as a useEffect cleanup.

export function pollWhenVisible(fn, ms) {
  if (typeof window === "undefined") return () => {};
  let timer = null;
  const start = () => {
    if (timer != null) return;
    timer = window.setInterval(fn, ms);
  };
  const stop = () => {
    if (timer != null) {
      window.clearInterval(timer);
      timer = null;
    }
  };
  const onVis = () => {
    if (document.visibilityState === "visible") {
      try { fn(); } catch { /* swallow — same contract as bare setInterval callbacks */ }
      start();
    } else {
      stop();
    }
  };
  // Initial state.
  if (document.visibilityState === "visible") start();
  document.addEventListener("visibilitychange", onVis);
  return () => {
    stop();
    document.removeEventListener("visibilitychange", onVis);
  };
}

export default pollWhenVisible;
